import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { StoreProvider, useStore } from "@/context/StoreContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import OrderSuccess from "@/pages/OrderSuccess";
import OrderCancel from "@/pages/OrderCancel";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import { Delivery, Returns, Legal, Privacy } from "@/pages/StaticPages";
import "@/App.css";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-ink flex flex-col items-center justify-center px-6 text-center">
          <p className="font-display font-extrabold uppercase text-3xl text-white">Une erreur est survenue</p>
          <button onClick={() => { this.setState({ error: null }); window.location.href = "/"; }} className="mt-8 bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-8 py-3">
            Retour à l'accueil
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
};

const Chrome = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <Navbar />}
      {children}
      {!isAdmin && <Footer />}
      {!isAdmin && <CartDrawer />}
    </>
  );
};

const Shell = () => {
  const { setDrawerOpen } = useStore();
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const onKey = (e) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.removeEventListener("keydown", onKey);
    };
  }, [setDrawerOpen]);

  return (
    <Chrome>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/boutique" element={<Shop />} />
        <Route path="/produit/:slug" element={<ProductDetail />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/commande/succes" element={<OrderSuccess />} />
        <Route path="/commande/annule" element={<OrderCancel />} />
        <Route path="/livraison" element={<Delivery />} />
        <Route path="/retours" element={<Returns />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/mentions-legales" element={<Legal />} />
        <Route path="/confidentialite" element={<Privacy />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </Chrome>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <StoreProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="bottom-center" theme="dark" />
        </BrowserRouter>
      </StoreProvider>
    </ErrorBoundary>
  );
}

export default App;
